// AU18 (docs/build-briefs/AU18-location-audit-log.md). The Activity tab of the Audit log: what happened at
// one location, newest first, drawn inside the operations frame ui/src/admin.ts owns. This module owns what is
// inside the Activity tab: the location picker, the filters, the list and the CSV download. The Settings
// changes tab is the AU17 screen (ui/src/changeHistory.ts), unchanged.
//
// Admin surface: a person on a computer or a tablet. Every control is 44px or taller and nothing depends
// on hover. Text is set with textContent only (names and notes are user entered). Nothing here can change
// a record: it reads, and it can write nothing.
//
// States: loading, the list, N/A (nothing matches), error with Try again, and no access (a 403, which is
// also what a person whose permission was just taken away sees).

import { SERVICE_UNAVAILABLE_SENTENCE } from "./serviceUnavailable.ts";
import {
  getAuditLog,
  getAuditLogCsv,
  type AuditLogEntryDto,
  type AuditLogLinkDto,
  type AuditLogPageDto,
} from "./api.ts";
import {
  AUDIT_LOG_RANGE_CHIPS,
  auditFiltersActive,
  auditLogQuery,
  defaultAuditFilters,
  downloadName,
  groupAuditByDay,
  initialsOf,
  linkHref,
  linkLabel,
  showingLine,
  spanLine,
  type AuditLogFilters,
  type AuditLogRangeId,
} from "./auditLogModel.ts";
import { createExportMenu } from "./exportMenu.ts";
import { deliverExportFile } from "./googleSheets.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
import { hasPermission, type Session } from "./session.ts";
import { sortOptions } from "./nameSort.ts";

const SVG_NS = "http://www.w3.org/2000/svg";

type Status = "idle" | "loading" | "ready" | "error" | "no-access";

export interface AuditLogOptions {
  /** Whether this session can open the screen a row's link names (the link is left off otherwise). */
  canOpenLink: (session: Session, link: AuditLogLinkDto) => boolean;
  /** TEN11-S5: filters to start from (the Support access tab's link), over the defaults. */
  initial?: Partial<AuditLogFilters>;
}

interface State {
  session: Session | undefined;
  options: AuditLogOptions | undefined;
  filters: AuditLogFilters;
  status: Status;
  errorMessage: string;
  page: AuditLogPageDto | undefined;
  entries: AuditLogEntryDto[];
  nextCursor: string | null;
  loadingMore: boolean;
  /** Bumped on every load and on reset, so an answer that arrives late is ignored. */
  generation: number;
}

const state: State = {
  session: undefined,
  options: undefined,
  filters: defaultAuditFilters(),
  status: "idle",
  errorMessage: "",
  page: undefined,
  entries: [],
  nextCursor: null,
  loadingMore: false,
  generation: 0,
};

interface Parts {
  root: HTMLElement;
  filtersCard: HTMLElement;
  downloadButton: HTMLButtonElement;
  downloadNote: HTMLElement;
  chipRow: HTMLElement;
  customRow: HTMLElement;
  fromInput: HTMLInputElement;
  toInput: HTMLInputElement;
  locationField: HTMLElement;
  locationSelect: HTMLSelectElement;
  personSelect: HTMLSelectElement;
  typeSelect: HTMLSelectElement;
  summary: HTMLElement;
  results: HTMLElement;
}

let parts: Parts | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  if (className !== undefined && className !== "") element.className = className;
  if (testId !== undefined) element.dataset["testid"] = testId;
  if (text !== undefined) element.textContent = text;
  return element;
}

function icon(kind: "lock" | "download" | "chevron"): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  const path = document.createElementNS(SVG_NS, "path");
  if (kind === "lock") {
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("class", "ch-ico");
    path.setAttribute("d", "M5 9V6.5a5 5 0 0 1 10 0V9M4 9h12v8H4z");
  } else if (kind === "download") {
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("class", "ch-ico");
    path.setAttribute("d", "M10 3v10m0 0-4-4m4 4 4-4M4 17h12");
  } else {
    svg.setAttribute("viewBox", "0 0 10 16");
    svg.setAttribute("class", "ch-chev");
    path.setAttribute("d", "M2 2l6 6-6 6");
  }
  svg.append(path);
  return svg;
}

// ---------------------------------------------------------------------------------------------
// Building the fixed parts, once
// ---------------------------------------------------------------------------------------------

function field(label: string, control: HTMLElement, id: string): HTMLElement {
  const wrap = el("div", "ch-fld");
  const labelEl = el("label", "", undefined, label);
  labelEl.htmlFor = id;
  control.id = id;
  wrap.append(labelEl, control);
  return wrap;
}

function buildOnce(container: HTMLElement): Parts {
  container.textContent = "";
  const root = el("div", "ch-root al-root", "audit-log-root");

  const downloadButton = el("button", "ch-btn-secondary", "audit-log-download-csv");
  downloadButton.type = "button";
  downloadButton.append(icon("download"), document.createTextNode("Export"));
  const downloadNote = el("p", "ch-download-note", "audit-log-download-note");
  downloadNote.setAttribute("role", "status");

  const filters = el("div", "ch-card ch-filters", "audit-log-filters");
  const chipRow = el("div", "ch-rangebar");
  chipRow.setAttribute("role", "group");
  chipRow.setAttribute("aria-label", "Date range");
  for (const chip of AUDIT_LOG_RANGE_CHIPS) {
    const button = el("button", "ch-chip", `audit-log-chip-${chip.id}`, chip.label);
    button.type = "button";
    button.dataset["range"] = chip.id;
    button.addEventListener("click", () => handleRangeChip(chip.id));
    chipRow.append(button);
  }
  const rowActions = el("div", "ch-rangebar-actions");
  rowActions.append(downloadNote, downloadButton);
  // The menu wraps the button where it now sits, so it is built after the button is placed.
  createExportMenu({
    trigger: downloadButton,
    testIdPrefix: "audit-log-export",
    menuActive: () => true,
    googleSheet: () => state.session,
    onPick: (format) => void handleDownload(format),
    onPlain: () => void handleDownload("csv"),
  });
  chipRow.append(rowActions);

  const customRow = el("div", "ch-custom-row", "audit-log-custom-row");
  customRow.hidden = true;
  const fromInput = el("input", "ch-txt ch-date", "audit-log-from");
  fromInput.type = "date";
  const toInput = el("input", "ch-txt ch-date", "audit-log-to");
  toInput.type = "date";
  customRow.append(
    field("From", fromInput, "audit-log-from-input"),
    field("To", toInput, "audit-log-to-input"),
  );
  const onCustomChange = (): void => {
    state.filters.from = fromInput.value;
    state.filters.to = toInput.value;
    if (fromInput.value !== "" && toInput.value !== "") void load();
  };
  fromInput.addEventListener("change", onCustomChange);
  toInput.addEventListener("change", onCustomChange);

  const pickRow = el("div", "ch-pickrow al-pickrow");
  const locationSelect = el("select", "ch-sel", "audit-log-location");
  locationSelect.addEventListener("change", () => {
    state.filters.locationId = locationSelect.value;
    // People differ by location, so a person chosen at another location is dropped.
    state.filters.person = "";
    void load();
  });
  const locationField = field("Location", locationSelect, "audit-log-location-select");
  locationField.hidden = true;
  const personSelect = el("select", "ch-sel", "audit-log-person");
  personSelect.addEventListener("change", () => {
    state.filters.person = personSelect.value;
    void load();
  });
  const typeSelect = el("select", "ch-sel", "audit-log-type");
  typeSelect.addEventListener("change", () => {
    state.filters.type = typeSelect.value;
    void load();
  });
  pickRow.append(
    locationField,
    field("Person", personSelect, "audit-log-person-select"),
    field("Type", typeSelect, "audit-log-type-select"),
  );
  filters.append(chipRow, customRow, pickRow);

  const summary = el("div", "ch-summary", "audit-log-summary");
  const results = el("div", "ch-card ch-results", "audit-log-results");

  root.append(filters, summary, results);
  container.append(root);
  return {
    root,
    filtersCard: filters,
    downloadButton,
    downloadNote,
    chipRow,
    customRow,
    fromInput,
    toInput,
    locationField: locationField,
    locationSelect: locationSelect,
    personSelect,
    typeSelect,
    summary,
    results,
  };
}

// ---------------------------------------------------------------------------------------------
// Filter handlers
// ---------------------------------------------------------------------------------------------

function handleRangeChip(range: AuditLogRangeId): void {
  state.filters.range = range;
  if (range !== "custom") {
    state.filters.from = "";
    state.filters.to = "";
    void load();
    return;
  }
  if (state.page?.from !== undefined && state.page.to !== undefined) {
    state.filters.from = state.page.from;
    state.filters.to = state.page.to;
  }
  syncFilterControls();
  if (state.filters.from !== "" && state.filters.to !== "") void load();
}

// ---------------------------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------------------------

function fillSelect(
  select: HTMLSelectElement,
  options: readonly { value: string; label: string }[],
  chosen: string,
): void {
  const signature = options.map((o) => `${o.value}\u0000${o.label}`).join("\u0001");
  if (select.dataset["signature"] !== signature) {
    select.textContent = "";
    for (const option of options) {
      const node = document.createElement("option");
      node.value = option.value;
      node.textContent = option.label;
      select.append(node);
    }
    select.dataset["signature"] = signature;
  }
  select.value = chosen;
}

function syncFilterControls(): void {
  if (parts === undefined) return;
  const p = parts;
  for (const button of Array.from(
    p.chipRow.querySelectorAll<HTMLButtonElement>("button.ch-chip"),
  )) {
    const on = button.dataset["range"] === state.filters.range;
    button.classList.toggle("ch-on", on);
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }
  p.customRow.hidden = state.filters.range !== "custom";
  p.fromInput.value = state.filters.from;
  p.toInput.value = state.filters.to;
  const page = state.page;
  if (page !== undefined) {
    // The location picker exists only for a customer with more than one location the person may read.
    p.locationField.hidden = page.locations.length < 2;
    fillSelect(
      p.locationSelect,
      page.locations.map((siteLocation) => ({ value: siteLocation.id, label: siteLocation.name })),
      page.locationId ?? "",
    );
    fillSelect(
      p.personSelect,
      sortOptions(
        [
          { value: "", label: "Everyone" },
          ...(page.people ?? []).map((x) => ({ value: x.key, label: x.name })),
        ],
        (option) => option.value === "",
      ),
      state.filters.person,
    );
    fillSelect(
      p.typeSelect,
      [
        { value: "", label: "All types" },
        ...sortOptions(
          (page.actions ?? []).map((x) => ({ value: x.id, label: x.label })),
          () => false,
        ),
      ],
      state.filters.type,
    );
  }
  p.personSelect.classList.toggle("ch-on", state.filters.person !== "");
  p.typeSelect.classList.toggle("ch-on", state.filters.type !== "");
  p.downloadButton.disabled = state.status !== "ready";
  // EXP1-S4: a file needs report.export as well as the view permission, so the button is not offered without it.
  p.downloadButton.hidden =
    state.session === undefined || !hasPermission(state.session, "report.export");
  p.filtersCard.hidden = state.status === "no-access";
}

function renderSummary(): void {
  if (parts === undefined) return;
  parts.summary.textContent = "";
  if (state.status !== "ready" || state.page === undefined) return;
  const left = el("span", "ch-summary-left");
  left.append(
    el("span", "", "audit-log-count", showingLine(state.entries.length, state.nextCursor !== null)),
  );
  const span = spanLine(state.page);
  if (span !== "") left.append(el("span", "ch-range-span", "audit-log-range-span", span));
  parts.summary.append(left, el("span", "", undefined, "Newest first"));
}

function avatar(entry: AuditLogEntryDto): HTMLElement {
  return el(
    "span",
    `ch-avatar ch-avatar-sm${entry.actor.kind === "user" ? "" : " ch-avatar-gray"}`,
    undefined,
    initialsOf(entry.actor.name, entry.actor.kind),
  );
}

function renderRow(entry: AuditLogEntryDto): HTMLElement {
  const row = el("div", "ch-row al-row", "audit-log-row");
  row.dataset["entryId"] = entry.id;
  row.dataset["action"] = entry.action.id;

  const when = el("span", "ch-when", undefined, entry.timeLabel);
  const who = el("span", "ch-who");
  who.append(avatar(entry), el("span", "ch-nm", undefined, entry.actor.name));
  const typeCell = el("span", "ch-areacell");
  typeCell.append(
    el(
      "span",
      entry.family.id === "people" ? "ch-tag ch-tag-access" : "ch-tag",
      "audit-log-type-tag",
      entry.action.label,
    ),
  );
  const what = el("span", "ch-what al-what");
  what.append(el("span", "ch-what-text", "audit-log-sentence", entry.summary));
  if (entry.detail !== null) {
    what.append(el("span", "al-detail", "audit-log-detail", entry.detail));
  }
  const mtop = el("span", "ch-mtop");
  mtop.append(
    el("b", "", undefined, entry.timeLabel),
    avatar(entry),
    document.createTextNode(entry.actor.name),
  );
  row.append(when, who, typeCell, mtop, what);

  const link = entry.link;
  const session = state.session;
  const options = state.options;
  const href =
    link !== null && session !== undefined && options?.canOpenLink(session, link) === true
      ? linkHref(link)
      : undefined;
  if (link !== null && href !== undefined) {
    const anchor = el("a", "al-open", "audit-log-link");
    anchor.href = href;
    anchor.dataset["linkKind"] = link.kind;
    anchor.setAttribute("aria-label", linkLabel(link));
    anchor.append(icon("chevron"));
    row.append(anchor);
  } else {
    row.append(el("span", "al-open al-open-none"));
  }
  return row;
}

function headRow(): HTMLElement {
  const head = el("div", "ch-head al-head");
  for (const label of ["When", "Who", "Type", "What happened"]) {
    head.append(el("span", "", undefined, label));
  }
  head.append(el("span"));
  return head;
}

function renderResults(): void {
  if (parts === undefined) return;
  const p = parts;
  p.results.textContent = "";
  p.results.classList.remove("ch-results-empty");

  if (state.status === "loading") {
    const note = el("div", "ch-loadnote", "audit-log-loading");
    note.setAttribute("role", "status");
    note.append(el("span", "ch-spin"), document.createTextNode("Loading events."));
    p.results.append(headRow(), note);
    for (let i = 0; i < 5; i += 1) {
      const skel = el("div", "ch-skel al-skel");
      for (const cls of ["ch-bar ch-w3", "ch-bar ch-w4", "ch-bar ch-w1", "ch-bar ch-w2"]) {
        skel.append(el("span", cls));
      }
      p.results.append(skel);
    }
    return;
  }

  if (state.status === "no-access") {
    p.results.classList.add("ch-results-empty");
    const box = el("div", "ch-empty", "audit-log-no-access");
    const badge = el("div", "ch-badge");
    badge.append(icon("lock"));
    box.append(
      badge,
      el("h2", "", undefined, "You do not have access to the audit log"),
      el("p", "", undefined, 'Ask an admin to turn on "See the change history" for you.'),
    );
    p.results.append(box);
    return;
  }

  if (state.status === "error") {
    p.results.classList.add("ch-results-empty");
    const box = el("div", "ch-empty", "audit-log-error");
    box.setAttribute("role", "alert");
    box.append(
      el("h2", "", undefined, "Could not load the audit log"),
      el("p", "", undefined, state.errorMessage),
    );
    const retry = el("button", "ch-btn-primary", "audit-log-retry", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void load());
    const acts = el("div", "ch-acts");
    acts.append(retry);
    box.append(acts);
    p.results.append(box);
    return;
  }

  const page = state.page;
  if (state.status !== "ready" || page === undefined) return;

  if (state.entries.length === 0) {
    p.results.classList.add("ch-results-empty");
    const box = el("div", "ch-empty al-empty", "audit-log-empty");
    box.dataset["filtered"] = auditFiltersActive(state.filters) ? "true" : "false";
    box.append(
      el(
        "h2",
        "",
        undefined,
        auditFiltersActive(state.filters)
          ? "No entries match these filters."
          : "No changes recorded yet. Entries appear here as people change settings.",
      ),
    );
    p.results.append(box);
    return;
  }

  p.results.append(headRow());
  for (const group of groupAuditByDay(state.entries, page.todayLocal ?? "")) {
    const day = el("div", "ch-day", "audit-log-day");
    day.append(el("span", "", undefined, group.heading), el("small", "", undefined, group.sub));
    p.results.append(day);
    for (const entry of group.entries) p.results.append(renderRow(entry));
  }
  const foot = el("div", "ch-foot", "audit-log-footer");
  foot.append(
    el(
      "div",
      "",
      "audit-log-showing",
      showingLine(state.entries.length, state.nextCursor !== null),
    ),
  );
  if (state.nextCursor !== null) {
    const more = el(
      "button",
      "ch-btn-secondary",
      "audit-log-show-more",
      state.loadingMore ? "Loading..." : "Show 50 more",
    );
    more.type = "button";
    more.disabled = state.loadingMore;
    more.addEventListener("click", () => void loadMore());
    foot.append(more);
  }
  const lock = el("div", "ch-lockline");
  lock.append(icon("lock"), document.createTextNode("These records cannot be edited or deleted."));
  foot.append(lock);
  p.results.append(foot);
}

// ---------------------------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------------------------

function failureMessage(
  status: number,
  networkError: boolean,
  message: string | undefined,
): string {
  if (networkError || status === 0) {
    return "Could not reach the server. Check your connection and try again.";
  }
  if (status === 503 && message === SERVICE_UNAVAILABLE_SENTENCE) {
    return SERVICE_UNAVAILABLE_SENTENCE;
  }
  if (status >= 500 || message === undefined) {
    return "Something went wrong. Try again.";
  }
  return capitalizeServerMessage(message);
}

async function load(): Promise<void> {
  const session = state.session;
  if (session === undefined || parts === undefined) return;
  state.generation += 1;
  const generation = state.generation;
  state.status = "loading";
  parts.downloadNote.textContent = "";
  syncFilterControls();
  renderSummary();
  renderResults();
  const result = await getAuditLog(
    session.apiBaseUrl,
    session.tenantId,
    auditLogQuery(state.filters),
  );
  if (generation !== state.generation) return;
  if (result.status === 403 && result.body.error !== "location_not_permitted") {
    state.status = "no-access";
  } else if (result.status !== 200 || result.body.entries === undefined) {
    state.status = "error";
    state.errorMessage = failureMessage(
      result.status,
      result.networkError === true,
      result.body.message,
    );
  } else {
    state.status = "ready";
    state.page = result.body;
    state.entries = result.body.entries;
    state.nextCursor = result.body.nextCursor;
    // The server chose the location when none was named; keep it so Show more and the file follow the same location.
    if (state.filters.locationId === "" && result.body.locationId !== null) {
      state.filters.locationId = result.body.locationId;
    }
    if (state.filters.range === "custom" && result.body.from !== undefined) {
      state.filters.from = result.body.from;
      state.filters.to = result.body.to ?? result.body.from;
    }
  }
  syncFilterControls();
  renderSummary();
  renderResults();
}

async function loadMore(): Promise<void> {
  const session = state.session;
  if (session === undefined || state.nextCursor === null || state.loadingMore) return;
  const generation = state.generation;
  state.loadingMore = true;
  renderResults();
  const result = await getAuditLog(
    session.apiBaseUrl,
    session.tenantId,
    auditLogQuery(state.filters, { cursor: state.nextCursor }),
  );
  if (generation !== state.generation) return;
  state.loadingMore = false;
  if (result.status === 403) {
    state.status = "no-access";
  } else if (result.status !== 200 || result.body.entries === undefined) {
    state.status = "error";
    state.errorMessage = failureMessage(
      result.status,
      result.networkError === true,
      result.body.message,
    );
  } else {
    const known = new Set(state.entries.map((e) => e.id));
    state.entries = [...state.entries, ...result.body.entries.filter((e) => !known.has(e.id))];
    state.nextCursor = result.body.nextCursor;
  }
  renderSummary();
  renderResults();
}

async function handleDownload(format: GateExportFormat): Promise<void> {
  const session = state.session;
  if (session === undefined || parts === undefined || state.page === undefined) return;
  const generation = state.generation;
  parts.downloadNote.textContent = "Preparing the file...";
  parts.downloadButton.disabled = true;
  const page = state.page;
  const result = await getAuditLogCsv(
    session.apiBaseUrl,
    session.tenantId,
    auditLogQuery(state.filters, { format }),
  );
  if (generation !== state.generation || parts === undefined) return;
  parts.downloadButton.disabled = false;
  if (result.status !== 200 || result.blob === undefined) {
    parts.downloadNote.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(
    parts.downloadNote,
    result.blob,
    downloadName(page, "xlsx").replace(/\.xlsx$/, ""),
    format,
  );
}

// ---------------------------------------------------------------------------------------------
// Entry points for ui/src/admin.ts
// ---------------------------------------------------------------------------------------------

/** Opens the Activity tab inside `container` on the default range with no filters, at the session's location. */
export async function openAuditLog(
  session: Session,
  container: HTMLElement,
  options: AuditLogOptions,
): Promise<void> {
  resetAuditLog();
  state.session = session;
  state.options = options;
  state.filters = { ...defaultAuditFilters(), ...(options.initial ?? {}) };
  parts = buildOnce(container);
  await load();
}

/** A fresh permission snapshot arrived: nothing is reloaded, the server decides on the next read. */
export function refreshAuditLogSession(session: Session): void {
  state.session = session;
}

/** Sign-out and leaving the screen: forget the list (it is one location's whole history) so the next person on
 * a shared device sees nothing of it. */
export function resetAuditLog(): void {
  state.generation += 1;
  state.session = undefined;
  state.options = undefined;
  state.filters = defaultAuditFilters();
  state.status = "idle";
  state.errorMessage = "";
  state.page = undefined;
  state.entries = [];
  state.nextCursor = null;
  state.loadingMore = false;
  if (parts !== undefined) {
    parts.root.remove();
    parts = undefined;
  }
}
